import { NgForOf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption, MatChipSelectionChange } from '@angular/material/chips';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';

import { TranslateModule } from '@ngx-translate/core';

import { Region } from '../../../../models/patient-records.model';
import { ExtendedRecord, ExtendedRegion, SortingType } from '../../med-view.component';

@Component({
  selector: 'app-interactive-header',
  templateUrl: './interactive-header.component.html',
  styleUrls: ['./interactive-header.component.scss'],
  standalone: true,
  imports: [
    MatChipOption,
    NgForOf,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatChipListbox,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    TranslateModule,
  ],
})
export class InteractiveHeaderComponent implements OnInit, OnChanges {
  @Input({ required: true }) regions!: ExtendedRegion[];
  @Input({ required: true }) records!: ExtendedRecord[];
  @Input() currentSortingType: SortingType = SortingType.ByDocumentType;

  @Output() changeSelectedRegion: EventEmitter<ExtendedRegion> = new EventEmitter<ExtendedRegion>();
  @Output() changeSelectedRecord: EventEmitter<ExtendedRecord> = new EventEmitter<ExtendedRecord>();
  @Output() changeSortingType: EventEmitter<SortingType> = new EventEmitter<SortingType>();
  @Output() openLaborViewer: EventEmitter<void> = new EventEmitter<void>();
  @Output() openNurseFormular: EventEmitter<void> = new EventEmitter<void>();
  @Output() openDoctorFormular: EventEmitter<void> = new EventEmitter<void>();
  @Output() openTextEditor: EventEmitter<void> = new EventEmitter<void>();

  public disableLabViewerButton: boolean = true;
  public sortingTypes = SortingType;

  constructor() {}

  ngOnInit(): void {
    this.addFixedRegions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regions']) {
      this.addFixedRegions();
    }
  }

  private addFixedRegions(): void {
    const fixedRegions: ExtendedRegion[] = [
      {
        id: 10000,
        short_text: 'Krakenblatt',
        code: 'fixed_krakenblatt',
        selected: false,
        isDefault: true,
      },
      {
        id: 10001,
        short_text: 'Funktion',
        code: 'fixed_funktion',
        selected: false,
        isDefault: true,
      },
      { id: 10002, short_text: 'OP', code: 'fixed_op', selected: false, isDefault: true },
      { id: 10003, short_text: 'Labor', code: 'fixed_labor', selected: false, isDefault: true },
      {
        id: 10004,
        short_text: 'Mikrobiologie',
        code: 'fixed_mikrobiologie',
        selected: false,
        isDefault: true,
      },
      {
        id: 10005,
        short_text: 'Pathologie',
        code: 'fixed_pathologie',
        selected: false,
        isDefault: true,
      },
      {
        id: 10006,
        short_text: 'Radiologie',
        code: 'fixed_radiologie',
        selected: false,
        isDefault: true,
      },
      {
        id: 10007,
        short_text: 'Tumorkonferenz',
        code: 'fixed_tumorkonferenz',
        selected: false,
        isDefault: true,
      },
      { id: 10008, short_text: 'Konsile', code: 'fixed_konsile', selected: false, isDefault: true },
      {
        id: 10009,
        short_text: 'Dokumente',
        code: 'fixed_dokumente',
        selected: false,
        isDefault: true,
      },
    ];

    const dynamicRegionNameMap = new Map();

    this.regions.forEach(region => {
      if (!region.code?.startsWith('fixed_')) {
        const name = (region.long_text || region.short_text || '').toLowerCase();
        dynamicRegionNameMap.set(name, region);
      }
    });

    const filteredFixedRegions = fixedRegions.filter(fixedRegion => {
      const fixedName = (fixedRegion.long_text || fixedRegion.short_text || '').toLowerCase();
      return !dynamicRegionNameMap.has(fixedName);
    });

    const nonFixedRegions = this.regions.filter(r => !r.code?.startsWith('fixed_'));

    this.regions = [...nonFixedRegions, ...filteredFixedRegions];
  }

  public selectRegion(region: ExtendedRegion) {
    if (!region.selected) {
      this.disableLabViewerButton = region.code !== '23';
      this.changeSelectedRegion.emit(region);
    }
  }

  public selectRecord(record: ExtendedRecord) {
    this.changeSelectedRecord.emit(record);
  }

  public onSortingTypeChange(event: any): void {
    const sortingType = event.value;
    this.changeSortingType.emit(sortingType);
  }

  public getSelectedRecord(): ExtendedRecord | undefined {
    return this.records.find((r: ExtendedRecord) => r.selected);
  }

  public clickOnOpenLaborViewer($event: MatChipSelectionChange) {
    const laborRegion: ExtendedRegion | undefined = this.regions.find(
      (r: Region) => r.code === '23',
    );
    if ($event.selected) {
      if (laborRegion) {
        laborRegion.selected = false;
        this.openLaborViewer.emit();
      }
    } else {
      if (laborRegion) {
        this.selectRegion(laborRegion);
      }
    }
  }

  public onClickOpenNurseFormular() {
    this.openNurseFormular.emit();
  }

  public onClickOpenDoctorFormular() {
    this.openDoctorFormular.emit();
  }

  public onClickOpenTextEditor() {
    this.openTextEditor.emit();
  }

  public hasRecordsForRegion(region: ExtendedRegion): boolean {
    if (!region.code?.startsWith('fixed_')) {
      return true;
    }

    const regionType = region.code.replace('fixed_', '');

    return this.records.some(record => {
      return Object.entries(record).some(([key, value]) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(regionType.toLowerCase());
        }
        return false;
      });
    });
  }
}
